import { inject, Injectable, signal } from '@angular/core';
import {
  getLanguage,
  getOrderUuid,
  getPaymentMethod,
  getSelectedExhibitionId,
  getShoppingStartTime,
  selectIsTicketAssignmentStepVisible,
  selectUserId
} from '@app/app.reducer';
import { FieldKey, OptionKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { FileIds } from '@core/data-access/models/file-upload.model';
import { AllPersonsAndFormValues, AssignedPersonFormValues } from '@core/data-access/models/form/form-provider.model';
import { FormName, FormValues, QuestionnaireFormValues } from '@core/data-access/models/form/form.model';
import { PersonType } from '@core/data-access/models/person.model';
import {
  Order,
  OrderAddress,
  OrderBillingAddress,
  OrderBuyerInformation,
  OrderParkingTicket,
  OrderQuestionnaireFields,
  OrderRequest,
  OrderTicket,
  OrderTicketHolder
} from '@core/data-access/models/save-order/order-request.model';
import {
  SelectedTicketSendingOptions,
  TicketSendingOption
} from '@core/data-access/models/shared/ticket-sending-options.model';
import { FormProvider } from '@core/data-access/services/form/form-provider.service';
import { ReCaptcha } from '@core/data-access/services/recaptcha';
import { selectSelectedTicketSendingOptions } from '@core/data-access/store/shared/shared.selectors';
import { selectSelectedSavedBillingAddressId } from '@core/features/buyer-information/store/buyer-information.selectors';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  BookingContingentReservation,
  BookingParkingReservation,
  BookingTariff,
  BookingWorkshop
} from '@products/models/booking.model';
import { PromoCodes } from '@products/models/smart-promotions';
import { TariffUuid } from '@products/models/tariff.model';
import { WorkshopId } from '@products/models/workshop.model';
import { BookingSmartPromotionService } from '@products/services/booking-smart-promotion.service';
import { HelperService } from '@store/helpers/helper.service';
import {
  getAllBookedProductsTotalPrice,
  getAllBookedTariffs
} from '@store/products/booking/product/product-booking.selectors';
import { selectRedeemedValidDiscountPromoCodes } from '@store/products/booking/smart-promotion/booking-smart-promotion.selectors';
import { getSelectedSendingOption } from '@store/products/product-selection/product-selection.selectors';
import {
  filter as _filter,
  map as _map,
  defaults,
  defaultTo,
  find,
  forEach,
  includes,
  isEmpty,
  isNil,
  reduce,
  some,
  unset,
  values
} from 'lodash';
import { DateTime } from 'luxon';
import { filter, map, withLatestFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class OrderPreparationService {
  readonly #store = inject(Store);
  readonly #translateService = inject(TranslateService);
  readonly #formProvider = inject(FormProvider);
  readonly #helperService = inject(HelperService);
  readonly #reCaptcha = inject(ReCaptcha);
  readonly #bookingSmartPromotionService = inject(BookingSmartPromotionService);

  #isOrderProcessing = signal(false);

  setIsOrderProcessing(isOrderProcessing: boolean) {
    this.#isOrderProcessing.set(isOrderProcessing);
  }

  get isOrderProcessing() {
    return this.#isOrderProcessing;
  }

  prepareSaveOrderRequest$(isAnonymousTicket = false) {
    return this.#formProvider.getAllPersonsAndFormValues$(this.#translateService).pipe(
      withLatestFrom(
        this.#store.select(getOrderUuid),
        this.#store.select(getPaymentMethod),
        this.#store.select(getShoppingStartTime),
        this.#store.select(selectIsTicketAssignmentStepVisible),
        this.#store.select(selectUserId),
        this.#store.select(getSelectedExhibitionId),
        this.#store.select(getLanguage),
        this.#store.select(getSelectedSendingOption),
        this.#store.select(selectSelectedTicketSendingOptions),
        this.#store.select(selectSelectedSavedBillingAddressId),
        this.#store.select(getAllBookedTariffs),
        this.#store.select(getAllBookedProductsTotalPrice),
        this.#store.select(selectRedeemedValidDiscountPromoCodes)
      ),
      map(
        ([
          allPersonsAndFormValues,
          orderUuid,
          paymentProvider,
          shoppingStartTime,
          isTicketAssignmentStepVisible,
          userId,
          eventId,
          userLanguage,
          orderTicketSendingMode,
          selectedTicketSendingOptions,
          selectedBillingAddressId,
          allBookedTariffs,
          allBookedProductsTotalPrice,
          redeemedValidDiscountPromoCodes
        ]) => ({
          allPersonsAndFormValues,
          orderUuid,
          paymentProvider,
          shoppingStartTime: shoppingStartTime?.toISOString(),
          isTicketAssignmentStepVisible,
          userId,
          eventId,
          userLanguage,
          orderTicketSendingMode,
          selectedTicketSendingOptions,
          selectedBillingAddressId,
          allBookedTariffs,
          orderPrice: allBookedProductsTotalPrice.totalPrice,
          discountedPrice: allBookedProductsTotalPrice.totalDiscountedPrice,
          promoCodes: this.#bookingSmartPromotionService.mapRedeemedPromoCodesToPromoCodes(
            redeemedValidDiscountPromoCodes
          )
        })
      ),
      filter(({ allBookedTariffs }) => !isEmpty(allBookedTariffs)),
      map(
        ({
          allPersonsAndFormValues,
          orderUuid,
          paymentProvider,
          shoppingStartTime,
          isTicketAssignmentStepVisible,
          userId,
          eventId,
          userLanguage,
          orderTicketSendingMode,
          selectedTicketSendingOptions,
          selectedBillingAddressId,
          allBookedTariffs,
          orderPrice,
          discountedPrice,
          promoCodes
        }) =>
          this.prepareOrderRequest(
            isAnonymousTicket,
            allPersonsAndFormValues,
            orderUuid,
            paymentProvider,
            shoppingStartTime,
            isTicketAssignmentStepVisible,
            userId!,
            eventId!,
            userLanguage,
            orderTicketSendingMode,
            selectedTicketSendingOptions,
            selectedBillingAddressId,
            allBookedTariffs,
            orderPrice,
            discountedPrice,
            promoCodes
          )
      )
    );
  }

  private prepareOrderRequest(
    isAnonymousTicket: boolean,
    allPersonsAndFormValues: AllPersonsAndFormValues,
    orderUuid: string,
    paymentProvider: string,
    shoppingStartTime: string,
    isTicketAssignmentStepVisible: boolean,
    userId: number,
    eventId: number,
    userLanguage: string,
    orderTicketSendingMode: TicketSendingOption,
    selectedTicketSendingOptions: SelectedTicketSendingOptions,
    selectedBillingAddressId: number,
    allBookedTariffs: BookingTariff[],
    orderPrice: number,
    discountedPrice: number,
    promoCodes: PromoCodes
  ) {
    const orderRequest: OrderRequest = {
      userAgent: this.#helperService.browserInfo(),
      googleReCaptchaToken: this.#reCaptcha.token,
      paymentProvider,
      shoppingStartTime,
      shoppingEndTime: new Date().toISOString(),
      day: new Date().toISOString(),
      order: this.prepareOrder(
        isAnonymousTicket,
        allPersonsAndFormValues,
        isTicketAssignmentStepVisible,
        orderUuid,
        userId,
        eventId,
        userLanguage,
        orderTicketSendingMode,
        selectedTicketSendingOptions,
        selectedBillingAddressId,
        allBookedTariffs,
        orderPrice,
        discountedPrice,
        promoCodes
      )
    };

    return orderRequest;
  }

  private prepareOrder(
    isAnonymousTicket: boolean,
    allPersonsAndFormValues: AllPersonsAndFormValues,
    isTicketAssignmentStepVisible: boolean,
    orderUuid: string,
    userId: number,
    eventId: number,
    userLanguage: string,
    orderTicketSendingMode: TicketSendingOption,
    selectedTicketSendingOptions: SelectedTicketSendingOptions,
    selectedBillingAddressId: number,
    allBookedTariffs: BookingTariff[],
    orderPrice: number,
    discountedPrice: number,
    promoCodes: PromoCodes
  ) {
    const { orderTickets, orderParkingTickets } = this.prepareOrderTicketsAndParkingTickets(
      allPersonsAndFormValues,
      selectedTicketSendingOptions,
      allBookedTariffs
    );

    const order: Order = {
      uuid: orderUuid,
      userId,
      eventId,
      userLanguage,
      buyer: this.prepareBuyerInformation(
        isAnonymousTicket,
        isTicketAssignmentStepVisible,
        allPersonsAndFormValues[FormName.BuyerInformation],
        allPersonsAndFormValues[FormName.BuyerPrivacyPolicy],
        orderTickets
      ),
      billingAddress: this.prepareBillingAddress(
        allPersonsAndFormValues[FormName.BuyerBillingAddress],
        selectedBillingAddressId
      ),
      tickets: orderTickets,
      parkingTickets: orderParkingTickets,
      orderTicketSendingMode,
      orderPrice: +orderPrice.toFixed(2),
      discountedPrice: +discountedPrice.toFixed(2),
      promoCodes,
      termsAndConditionsAccepted: this.prepareOrderTermsAndConditionsAccepted(
        allPersonsAndFormValues[FormName.ConfirmationCheckboxes]
      ),
      questionnaireFields: this.prepareOrderQuestionnaireFields(allPersonsAndFormValues[FormName.BuyerQuestionnaire])
    };

    this.removeOrderFields(order, allPersonsAndFormValues[FormName.BuyerInformation]);

    return order;
  }

  private prepareOrderTicketsAndParkingTickets(
    allPersonsAndFormValues: AllPersonsAndFormValues,
    selectedTicketSendingOptions: SelectedTicketSendingOptions,
    allBookedTariffs: BookingTariff[]
  ) {
    const orderTickets: OrderTicket[] = [];
    const orderParkingTickets: OrderParkingTicket[] = [];

    forEach(allBookedTariffs, (bookedTariff) => {
      orderTickets.push(
        ...this.prepareOrderTickets(
          allPersonsAndFormValues,
          selectedTicketSendingOptions,
          bookedTariff
        )
      );

      orderParkingTickets.push(...this.prepareOrderParkingTickets(bookedTariff));
    });

    return { orderTickets, orderParkingTickets };
  }

  private prepareOrderTickets(
    allPersonsAndFormValues: AllPersonsAndFormValues,
    selectedTicketSendingOptions: SelectedTicketSendingOptions,
    bookedTariff: BookingTariff
  ) {
    return reduce(
      bookedTariff.tariffUuids,
      (orderTickets, tariffUuid) => {
        orderTickets.push(
          this.prepareOrderTicket(
            allPersonsAndFormValues,
            selectedTicketSendingOptions,
            bookedTariff,
            tariffUuid
          )
        );

        return orderTickets;
      },
      [] as OrderTicket[]
    );
  }

  private prepareOrderTicket(
    allPersonsAndFormValues: AllPersonsAndFormValues,
    selectedTicketSendingOptions: SelectedTicketSendingOptions,
    bookedTariff: BookingTariff,
    tariffUuid: TariffUuid
  ): OrderTicket {
    const {
      ticketPersonId,
      ticketTypeId,
      ticketPersonTypeId,
      packageNumber,
      packageIndex,
      voucherCode,
      workshops,
      contingentReservations,
      parkingReservations
    } = bookedTariff;

    const orderTicket: OrderTicket = {
      ticketPersonId,
      tariffDocumentIds: this.setUploadedTariffDocumentIds(allPersonsAndFormValues, tariffUuid),
      groupId: ticketTypeId,
      ticketTypeId: ticketPersonTypeId,
      ticketHolder: this.setOrderTicketHolder(allPersonsAndFormValues, tariffUuid),
      ticketSendingMode: this.setOrderTicketTicketSendingMode(
        allPersonsAndFormValues[FormName.TicketHolder][tariffUuid],
        selectedTicketSendingOptions[tariffUuid]
      ),
      workshops: this.setOrderTicketWorkshops(workshops, tariffUuid),
      day: this.setOrderTicketContingentReservationDay(contingentReservations, tariffUuid),
      licensePlate: this.setOrderTicketLicensePlate(parkingReservations, tariffUuid)
    };

    if (!isNil(packageNumber)) {
      orderTicket.packageNumber = packageNumber;
      orderTicket.shoppingCartPkgIndex = packageIndex;
    }

    if (!isEmpty(voucherCode)) {
      orderTicket.voucherCode = voucherCode;
    }

    return orderTicket;
  }

  private setOrderTicketHolder(
    allPersonsAndFormValues: AllPersonsAndFormValues,
    tariffUuid: TariffUuid
  ) {
    if (isEmpty(allPersonsAndFormValues[FormName.TicketHolder])) {
      return null;
    }

    const { formValues: buyerFormValues } = allPersonsAndFormValues[FormName.BuyerInformation];
    const { person: ticketHolderPerson, formValues: ticketHolderFormValues } =
      allPersonsAndFormValues[FormName.TicketHolder][tariffUuid];

    const isBuyerAVisitor = defaultTo(
      ticketHolderPerson?.type === PersonType.Buyer || ticketHolderPerson?.type === PersonType.BuyerIsHolder,
      false
    );

    const formValues = isBuyerAVisitor ? defaults({}, ticketHolderFormValues, buyerFormValues) : ticketHolderFormValues;

    const orderTicketHolder: OrderTicketHolder = {
      personId: ticketHolderPerson?.id,
      personUuid: ticketHolderPerson?.uuid,
      gender: defaultTo(formValues[FieldKey.Gender] as string, ''),
      title: defaultTo(formValues[FieldKey.Title] as string, ''),
      firstName: defaultTo(formValues[FieldKey.FirstName] as string, ''),
      lastName: defaultTo(formValues[FieldKey.LastName] as string, ''),
      dateOfBirth: defaultTo(formValues[FieldKey.DateOfBirth] as string, ''),
      function: defaultTo(formValues[FieldKey.Function] as string, ''),
      department: defaultTo(formValues[FieldKey.Department] as string, ''),
      occupationalGroup: defaultTo(formValues[FieldKey.OccupationalGroup] as string, ''),
      address: this.prepareOrderAddress(formValues),
      email: defaultTo(formValues[FieldKey.Email] as string, buyerFormValues[FieldKey.Email] as string),
      telephone: defaultTo(formValues[FieldKey.Telephone] as string, ''),
      isBuyerAVisitor,
      visitorQuestionnaire: this.prepareOrderQuestionnaireFields(
        allPersonsAndFormValues[FormName.TicketHolderQuestionnaire][tariffUuid]
      )
    };

    return orderTicketHolder;
  }

  private setOrderTicketTicketSendingMode(
    ticketHolderAssignedPersonFormValues: AssignedPersonFormValues,
    selectedTicketSendingOptions: TicketSendingOption
  ) {
    let isTicketToOwnerChecked = false;
    let sendingOption = selectedTicketSendingOptions;

    if (!isEmpty(ticketHolderAssignedPersonFormValues)) {
      const { formValues } = ticketHolderAssignedPersonFormValues;

      isTicketToOwnerChecked = formValues[OptionKey.ToOwner] as boolean;
    }

    if (sendingOption === TicketSendingOption.MobilePerOwner && !isTicketToOwnerChecked) {
      sendingOption = TicketSendingOption.MobilePerBuyer;
    }

    if (sendingOption === TicketSendingOption.TicketRetrievalLink && !isTicketToOwnerChecked) {
      sendingOption = TicketSendingOption.TicketRetrievalLinkBuyer;
    }

    return sendingOption;
  }

  private setOrderTicketContingentReservationDay(
    contingentReservations: BookingContingentReservation[],
    tariffUuid: TariffUuid
  ) {
    const contingentReservation = find(
      contingentReservations,
      (contingentReservation) => contingentReservation.tariffUuid === tariffUuid
    );

    return !isEmpty(contingentReservation) ? DateTime.fromJSDate(contingentReservation.fromDate).toISODate() : null;
  }

  private setOrderTicketLicensePlate(parkingReservations: BookingParkingReservation[], tariffUuid: TariffUuid) {
    const parkingReservation = find(
      parkingReservations,
      (parkingReservation) => parkingReservation.tariffUuid === tariffUuid
    );

    return defaultTo(parkingReservation?.licensePlateNumber, '');
  }

  private setUploadedTariffDocumentIds = (allPersonsAndFormValues: AllPersonsAndFormValues, tariffUuid: TariffUuid) => {
    return defaultTo(
      allPersonsAndFormValues[FormName.TicketHolder][tariffUuid]?.formValues[FieldKey.CV] as FileIds,
      []
    );
  };

  private setOrderTicketWorkshops(workshops: BookingWorkshop[], tariffUuid: TariffUuid): WorkshopId[] {
    return _map(
      _filter(workshops, ({ tariffUuids }) => includes(tariffUuids, tariffUuid)),
      'id'
    );
  }

  private prepareOrderParkingTickets(bookedTariff: BookingTariff) {
    return reduce(
      bookedTariff.amanoParkingReservations,
      (amanoParkingReservations, { since, until }) => {
        const { ticketTypeId, ticketPersonTypeId } = bookedTariff;

        amanoParkingReservations.push({
          groupId: ticketTypeId,
          ticketTypeId: ticketPersonTypeId,
          since: this.#helperService.toStringWithoutOffset(since),
          until: this.#helperService.toStringWithoutOffset(until)
        });

        return amanoParkingReservations;
      },
      []
    );
  }

  private prepareBuyerInformation(
    isAnonymousTicket: boolean,
    isTicketAssignmentStepVisible: boolean,
    buyerInformationPersonsAndFormValues: AssignedPersonFormValues,
    buyerPrivacyPolicyPersonAndFormValues: AssignedPersonFormValues,
    orderTickets: OrderTicket[]
  ): OrderBuyerInformation {
    const { person, formValues } = buyerInformationPersonsAndFormValues;
    const { formValues: privacyPolicyFormValues } = buyerPrivacyPolicyPersonAndFormValues;

    const isNewsletterChecked = isAnonymousTicket
      ? false
      : defaultTo(formValues[OptionKey.IsNewsletterChecked] as boolean, false);

    const isBuyerAVisitor =
      !isTicketAssignmentStepVisible || some(orderTickets, ({ ticketHolder }) => ticketHolder?.isBuyerAVisitor);

    return {
      personId: person?.id,
      vatNumber: defaultTo(formValues[FieldKey.VatNumber] as string, ''),
      gender: defaultTo(formValues[FieldKey.Gender] as string, ''),
      title: defaultTo(formValues[FieldKey.Title] as string, ''),
      firstName: defaultTo(formValues[FieldKey.FirstName] as string, ''),
      lastName: defaultTo(formValues[FieldKey.LastName] as string, ''),
      dateOfBirth: defaultTo(formValues[FieldKey.DateOfBirth] as string, ''),
      function: defaultTo(formValues[FieldKey.Function] as string, ''),
      department: defaultTo(formValues[FieldKey.Department] as string, ''),
      occupationalGroup: defaultTo(formValues[FieldKey.OccupationalGroup] as string, ''),
      address: this.prepareOrderAddress(formValues),
      email: defaultTo(formValues[FieldKey.Email] as string, ''),
      telephone: defaultTo(formValues[FieldKey.Telephone] as string, ''),
      privacyPolicyAccepted: defaultTo(privacyPolicyFormValues[OptionKey.DisclaimerConfirmation] as boolean, false),
      privacyPolicyOptionalAccepted: defaultTo(
        privacyPolicyFormValues[OptionKey.DisclaimerOptionalConfirmation] as boolean,
        false
      ),
      isFairCatalogChecked: defaultTo(formValues[OptionKey.IsFairCatalogChecked] as boolean, false),
      isNewsletterChecked,
      isBuyerAVisitor,
      visitorQuestionnaire: []
    };
  }

  private prepareOrderAddress(formValues: FormValues): OrderAddress {
    return {
      company: defaultTo(formValues[FieldKey.Company] as string, ''),
      street: defaultTo(formValues[FieldKey.Street] as string, ''),
      zipCode: defaultTo(formValues[FieldKey.ZipCode] as string, ''),
      city: defaultTo(formValues[FieldKey.City] as string, ''),
      country: defaultTo(formValues[FieldKey.Country] as string, '')
    };
  }

  private prepareBillingAddress(
    billingAddressPersonAndFormValues: AssignedPersonFormValues,
    selectedBillingAddressId: number
  ): OrderBillingAddress {
    const { formValues } = billingAddressPersonAndFormValues;

    const orderBillingAddress: OrderBillingAddress = {
      company: defaultTo(formValues[FieldKey.Company] as string, ''),
      country: defaultTo(formValues[FieldKey.Country] as string, ''),
      street: defaultTo(formValues[FieldKey.Street] as string, ''),
      city: defaultTo(formValues[FieldKey.City] as string, ''),
      zipCode: defaultTo(formValues[FieldKey.ZipCode] as string, ''),
      saveBillingAddress: defaultTo(formValues[OptionKey.SaveBillingAddress] as boolean, false)
    };

    if (!isNil(selectedBillingAddressId)) {
      orderBillingAddress.id = defaultTo(selectedBillingAddressId, null);
    }

    return orderBillingAddress;
  }

  private prepareOrderQuestionnaireFields(
    questionnairePersonAndFormValues: AssignedPersonFormValues<QuestionnaireFormValues>
  ): OrderQuestionnaireFields {
    if (isEmpty(questionnairePersonAndFormValues)) {
      return [];
    }

    const { formValues } = questionnairePersonAndFormValues;

    return values(formValues);
  }

  private prepareOrderTermsAndConditionsAccepted(confirmationCheckboxesFormValues: AssignedPersonFormValues): boolean {
    const { formValues } = confirmationCheckboxesFormValues;

    const termsAndConditionsAccepted = defaultTo(formValues[OptionKey.TradeConditions] as boolean, false);

    return termsAndConditionsAccepted;
  }

  private removeOrderFields(order: Order, buyerInformationPersonsAndFormValues: AssignedPersonFormValues) {
    const { formValues } = buyerInformationPersonsAndFormValues;

    if (!formValues[OptionKey.DifferentBillingAddress]) {
      unset(order, 'billingAddress');
    }
  }
}
